// Display metadata for platforms -- label, icon, example, and hint --
// layered on top of the shared canonical list
// (socialPlatformRegistry.js), which is what actually decides which
// platforms exist and how their links are built. This file only decides
// what a platform looks like and how to explain it once it's already
// known to be valid; anything needing validity instead reads
// socialPlatformRegistry.js directly (route/api/users.js's
// validateSocialEntries does, server-side).
//
// icon is a URL built by convention from the key
// (social-network-<key>.png, served from this app's own /images/ --
// see public/images/ and server.js's `express.static('public')`), not a
// per-platform code entry -- adding a platform's icon is just dropping
// the file in with the right name, nothing here needs to change for it.
import {
  SOCIAL_PLATFORM_LIST,
  SOCIAL_PLATFORM_URL_TEMPLATE_BY_KEY,
  inPaymentGroup,
} from './socialPlatformRegistry';

const iconUrl = (key) => `/images/social-network-${key}.png`;

// "custom" is deliberately not a member of this list -- it has no fixed
// icon/label/URL template/example/hint of its own (its label and value
// are exactly what the user types). Callers needing to special-case it
// check `platform === CUSTOM_PLATFORM_KEY` directly.
export const CUSTOM_PLATFORM_KEY = 'custom';

// One metadata entry per platform -- the only thing this file actually
// adds on top of socialPlatformRegistry.js. Adding a platform: one entry
// in the shared list (socialPlatformRegistry.js), one entry here, one
// icon file. Nothing else.
//
// example is what the value input shows as a placeholder -- written so
// the expected format is obvious without reading the hint: a bare
// username for handle-type platforms, a full +country-code number for
// phone/WhatsApp/Signal/landline, a full domain for website/link, a real
// email shape for s_email. hint is the one-sentence explanation the
// info icon reveals on tap, for whatever the example alone doesn't make
// obvious (mainly: which platforms want a bare handle vs. a full URL,
// and why a couple of these -- Discord, WeChat, Upwork -- aren't just
// "your username").
const PLATFORM_METADATA_BY_KEY = {
  instagram: { label: 'Instagram', example: 'yourname', hint: 'Your Instagram username, without the @.' },
  venmo: { label: 'Venmo', example: 'yourname', hint: 'Your Venmo username. The @ is optional -- it is removed for you.' },
  snapchat: { label: 'Snapchat', example: 'yourname', hint: 'Your Snapchat username.' },
  whatsapp: { label: 'WhatsApp', example: '+14155552671', hint: 'Your WhatsApp number, including the country code.' },
  whatsappbusiness: { label: 'WhatsApp Business', example: '+14155552671', hint: 'Your WhatsApp Business number, including the country code.' },
  phone: { label: 'Phone', example: '+14155552671', hint: 'Your phone number, including the country code.' },
  landline: { label: 'Landline', example: '+442071838750', hint: 'Your landline number, including the country code.' },
  twitter: { label: 'Twitter', example: 'yourname', hint: 'Your Twitter username, without the @.' },
  facebook: { label: 'Facebook', example: 'yourname', hint: 'Your Facebook username or profile ID.' },
  linkedin: { label: 'LinkedIn', example: 'https://linkedin.com/in/yourname', hint: 'Your full LinkedIn profile URL.' },
  youtube: { label: 'YouTube', example: 'https://youtube.com/@yourchannel', hint: 'Your full YouTube channel URL.' },
  tiktok: { label: 'TikTok', example: 'https://tiktok.com/@yourname', hint: 'Your full TikTok profile URL.' },
  twitch: { label: 'Twitch', example: 'yourname', hint: 'Your Twitch username.' },
  telegram: { label: 'Telegram', example: 'yourname', hint: 'Your Telegram username, without the @.' },
  pinterest: { label: 'Pinterest', example: 'yourname', hint: 'Your Pinterest username.' },
  applemusic: { label: 'Apple Music', example: 'yourname', hint: 'Your Apple Music artist or profile name.' },
  spotify: { label: 'Spotify', example: 'yourname', hint: 'Your Spotify username or artist ID.' },
  paypal: { label: 'PayPal', example: 'yourname', hint: 'Your PayPal.Me username -- the part after paypal.me/, without the slash.' },
  soundcloud: { label: 'SoundCloud', example: 'yourname', hint: 'Your SoundCloud username.' },
  website: { label: 'Website', example: 'yourdomain.com', hint: "Your website's domain, without https://." },
  link: { label: 'Link', example: 'yourdomain.com/page', hint: 'A domain and path, without https://.' },
  calendly: { label: 'Calendly', example: 'yourname', hint: 'Your Calendly username.' },
  s_email: { label: 'Email', example: 'you@example.com', hint: 'The email address you want shown on your profile.' },
  address: { label: 'Address', example: '123 Main St, Springfield', hint: "Your address as you want it displayed -- this isn't turned into a map link." },
  skype: { label: 'Skype', example: 'live:yourname', hint: 'Your Skype ID or live: handle.' },

  discord: {
    label: 'Discord',
    example: '123456789012345678',
    hint: "Your numeric Discord user ID (Discord doesn't support linking by username) -- copy it via User Settings > Advanced > enable Developer Mode, then right-click your name > Copy User ID.",
  },
  wechat: {
    label: 'WeChat',
    example: 'your_wechat_id',
    hint: 'Your WeChat ID -- WeChat has no public profile link, so this is shown as text for people to search for.',
  },
  threads: { label: 'Threads', example: 'yourname', hint: 'Your Threads username, without the @.' },
  x: { label: 'X', example: 'yourname', hint: 'Your X (formerly Twitter) username, without the @.' },
  signal: { label: 'Signal', example: '+14155552671', hint: 'Your Signal number, including the country code.' },
  upwork: {
    label: 'Upwork',
    example: '~017c1234567890abcd',
    hint: 'The ~id segment from your own Upwork profile URL, tilde included -- not your display name.',
  },
  fiverr: { label: 'Fiverr', example: 'yourname', hint: 'Your Fiverr username.' },
  behance: { label: 'Behance', example: 'yourname', hint: 'Your Behance username.' },

  // International payment links. Ordinary link entries -- they open a
  // page rather than exposing a value to copy -- so they get the same
  // label/example/hint treatment as every platform above and no `note`
  // (that field is for payment entries, which state a consequence of
  // saving). Each hint says the symbol is optional rather than "without
  // the $", because it genuinely is: normalizeHandle strips it on save.
  cashapp: {
    label: 'Cash App',
    example: 'yourcashtag',
    hint: 'Your $cashtag. The $ is optional -- it is removed for you.',
  },
  revolut: {
    label: 'Revolut',
    example: 'yourrevtag',
    hint: 'Your Revtag. The @ is optional -- it is removed for you.',
  },
  wise: {
    label: 'Wise',
    example: 'yourname1234',
    hint: 'Your Wisetag, the @handle on your Wise account. The @ is optional -- it is removed for you.',
  },

  // Payment entries (kind: 'payment' in socialPlatformRegistry.js).
  // These are copied, not opened, so `example` is the format to type and
  // `hint` explains what the value is for -- the same job as above.
  //
  // `note` is additional to hint and specific to payment entries: shown
  // inline in the editor as the user fills the field, not hidden behind
  // the info icon, because it describes a consequence of saving rather
  // than the input format.
  raast: {
    label: 'Raast ID',
    example: '+923001234567',
    hint: 'Your Raast ID -- the mobile number registered against your bank account. Raast works across every Pakistani bank, so you do not need a separate entry per bank.',
    note: 'Shown publicly on your profile so anyone can pay you.',
  },
  iban: {
    label: 'IBAN',
    example: 'PK36SCBL0000001123456702',
    hint: 'Your 24-character Pakistani IBAN. A Raast QR is generated from it, which scans in any Pakistani banking app.',
    // The disclosure a customer will not anticipate: IBAN alone resolves
    // to the account title in every banking app's transfer screen.
    note: 'Shown publicly on your profile. Anyone entering an IBAN in a banking app also sees the account title it belongs to.',
  },
  easypaisa: {
    label: 'Easypaisa',
    example: '+923001234567',
    hint: 'The mobile number registered against your Easypaisa account.',
    note: 'Shown publicly on your profile so anyone can pay you.',
  },
  jazzcash: {
    label: 'JazzCash',
    example: '+923001234567',
    hint: 'The mobile number registered against your JazzCash account.',
    note: 'Shown publicly on your profile so anyone can pay you.',
  },
  nayapay: {
    label: 'NayaPay',
    example: '+923001234567',
    hint: 'Your NayaPay ID, or the mobile number registered against your NayaPay account.',
    note: 'Shown publicly on your profile so anyone can pay you.',
  },
};

export const SOCIAL_PLATFORMS = SOCIAL_PLATFORM_LIST.map(({ key, urlTemplate }) => ({
  key,
  urlTemplate,
  icon: iconUrl(key),
  ...PLATFORM_METADATA_BY_KEY[key],
}));

export const SOCIAL_PLATFORM_BY_KEY = SOCIAL_PLATFORMS.reduce((acc, platform) => {
  acc[platform.key] = platform;
  return acc;
}, {});

// Falls back to a titlecased version of the key itself for anything not
// in the table (shouldn't happen for a real platform key, but keeps a
// stray/unmapped one from rendering as blank instead of just ugly).
const titleCase = (key) => key.charAt(0).toUpperCase() + key.slice(1);

export const platformLabel = (key) =>
  (PLATFORM_METADATA_BY_KEY[key] || {}).label || titleCase(key);

export const platformIcon = (key) => (SOCIAL_PLATFORM_BY_KEY[key] || {}).icon || iconUrl(key);

export const platformExample = (key) => (PLATFORM_METADATA_BY_KEY[key] || {}).example || '';

export const platformHint = (key) => (PLATFORM_METADATA_BY_KEY[key] || {}).hint || '';

// Shown inline in the editor for payment entries -- a one-line statement
// of what saving this value means publicly. Empty for every link
// platform, so the editor can render it unconditionally.
export const platformNote = (key) => (PLATFORM_METADATA_BY_KEY[key] || {}).note || '';

// entry.value is already a complete URL for a custom entry (enforced by
// the schema validator, see models/User.js) -- used as-is, no template.
export const platformUrl = (platform, value) => {
  if (platform === CUSTOM_PLATFORM_KEY) return value;
  const template = SOCIAL_PLATFORM_URL_TEMPLATE_BY_KEY[platform];
  return template ? template(value) : `https://${platform}.com/${value}`;
};

// Platforms whose value is a private contact channel (a phone number, a
// WhatsApp number, an email address) rather than a public handle or URL.
// Never shown as visible text to tell duplicates apart, unlike
// handle-type platforms (instagram, twitter, tiktok, a custom link,
// etc.) where the value is already meant to be public.
export const CONTACT_PLATFORM_KEYS = [
  'phone',
  'whatsapp',
  'whatsappbusiness',
  'landline',
  's_email',
];

// Payment methods render below the links, wherever the list is drawn.
// Applied at display time and never written back: `order` is the user's
// own arrangement, and rewriting it to match this would overwrite an
// intent we cannot recover and cannot undo. The stored value stays
// exactly as saved; only the sequence on screen changes.
//
// The grouping the picker uses decides this too, so it covers all ten
// payment methods rather than only the five copy-type ones -- PayPal and
// Venmo are payment methods that happen to open a URL, and Venmo sitting
// at the top of a profile purely because it predates the payment group
// is the case this fixes.
//
// Two filters rather than a sort comparator: partitioning is stable by
// construction, so each group keeps the relative order the user built,
// without depending on the engine's sort being stable.
//
// Every caller that renders the list must apply this before
// computeSocialDisplay below, since that resolves names by position and
// the two have to agree on what position means. 'custom' is not in the
// payment group, so custom links stay with the links.
export const sortEntriesForDisplay = (entries) => {
  const list = entries || [];
  return list
    .filter((entry) => !inPaymentGroup(entry.platform))
    .concat(list.filter((entry) => inPaymentGroup(entry.platform)));
};

// Resolves what ListItem should actually render for each entry of a
// socialList, in display order -- an entry's own label always wins (it's
// pinned to that one entry, so it disambiguates it on its own). Only
// entries that both share a platform *and* have no label of their own
// need any further disambiguation, and only entries in that same boat
// count towards "two or more share a platform" or consume a number --
// an entry that already has a label never needed one and never blocks
// another entry from getting "(2)".
//
// Handle-type platforms get the raw value as a second line (subtitle) --
// showing it is the point of a handle. Contact-type platforms
// (CONTACT_PLATFORM_KEYS) never get the value exposed as text; the name
// itself is numbered instead ("Phone", "Phone (2)", ...).
export const computeSocialDisplay = (entries) => {
  const ambiguousCountByPlatform = {};
  for (const entry of entries) {
    if (entry.label) continue;
    ambiguousCountByPlatform[entry.platform] = (ambiguousCountByPlatform[entry.platform] || 0) + 1;
  }

  const seenByPlatform = {};
  return entries.map((entry) => {
    const baseName = entry.label || platformLabel(entry.platform);

    if (entry.label || (ambiguousCountByPlatform[entry.platform] || 0) < 2) {
      return { name: baseName, subtitle: null };
    }

    seenByPlatform[entry.platform] = (seenByPlatform[entry.platform] || 0) + 1;
    const occurrence = seenByPlatform[entry.platform];

    if (CONTACT_PLATFORM_KEYS.includes(entry.platform)) {
      return {
        name: occurrence === 1 ? baseName : `${baseName} (${occurrence})`,
        subtitle: null,
      };
    }

    return { name: baseName, subtitle: entry.value };
  });
};
